<template>
  <v-dialog 
    :value="value" 
    @input="$emit('input', $event)" 
    max-width="500px" 
    persistent 
    scrollable
  >
    <v-card>
      <v-toolbar flat :color="isEditing ? 'orange' : 'teal'" dark dense>
        <v-toolbar-title>
          <v-icon left>{{ isEditing ? "mdi-pencil" : "mdi-plus-box" }}</v-icon>
          {{ isEditing ? "Editar Producto" : "Producto/Servicio Manual" }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pt-4">
        <v-form ref="productForm" v-model="formValid">
          <v-row dense>
            <!-- Información del producto si está editando -->
            <v-col cols="12" v-if="isEditing && productData.ean">
              <v-alert dense outlined type="info" class="mb-3">
                <div class="d-flex align-center">
                  <v-icon left>mdi-barcode</v-icon>
                  <span class="text-caption">
                    Código: {{ productData.ean }} | Ref: {{ productData.refe }}
                  </span>
                </div>
              </v-alert>
            </v-col>

            <!-- Nombre del producto/servicio -->
            <v-col cols="12">
              <v-text-field
                v-model="localProduct.nombre"
                label="Nombre del producto o servicio *"
                outlined
                dense
                :rules="validationRules.required"
                @keyup.enter="focusNext('descripcion')"
                ref="nombreProducto"
                :readonly="isEditing && !productData.manual"
              />
            </v-col>

            <!-- Descripción (solo para productos manuales) -->
            <v-col cols="12" v-if="showDescriptionField">
              <v-textarea
                v-model="localProduct.descripcion"
                label="Descripción detallada (opcional)"
                outlined
                dense
                rows="2"
                ref="descripcion"
                @keydown.enter.prevent="focusNext('cantidad')"
              />
            </v-col>

            <!-- Cantidad y Precio -->
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="localProduct.cantidad"
                label="Cantidad *"
                outlined
                dense
                type="number"
                min="1"
                :rules="validationRules.quantity"
                @keypress="filterNumbers"
                @keyup.enter="focusNext('precio')"
                ref="cantidad"
              >
                <template v-slot:append>
                  <v-btn
                    icon
                    x-small
                    @click="decrementQuantity"
                  >
                    <v-icon>mdi-minus</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click="incrementQuantity">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
              </v-text-field>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="localProduct.precio"
                label="Precio unitario *"
                outlined
                dense
                :prefix="currencySymbol"
                :rules="validationRules.price"
                @keypress="filterDecimals"
                @keyup.enter="focusNext('descuento')"
                ref="precio"
                :readonly="isEditing && !productData.manual"
              />
            </v-col>

            <!-- Descuento e IVA en una fila -->
            <v-col cols="6">
              <v-text-field
                v-model="localProduct.descuento"
                label="Descuento"
                outlined
                dense
                suffix="%"
                @keypress="filterNumbers"
                @keyup.enter="saveProduct"
                ref="descuento"
                hide-details
              />
            </v-col>
            <v-col cols="6" class="d-flex align-center">
              <v-chip small outlined class="ml-2">
                Impuesto: {{ localProduct.tax }}%
              </v-chip>
            </v-col>

            <!-- Notas compactas -->
            <v-col cols="12">
              <v-text-field
                v-model="localProduct.note"
                label="Notas"
                placeholder="Observaciones..."
                outlined
                dense
                ref="note"
                @keyup.enter="saveProduct"
                hide-details
              >
                <template v-slot:prepend-inner>
                  <v-icon small>mdi-note-text</v-icon>
                </template>
              </v-text-field>
            </v-col>

            <!-- Vista previa compacta -->
            <v-col cols="12" v-if="calculations.subtotal > 0">
              <div class="d-flex justify-space-between align-center py-2 px-3 grey lighten-4 rounded">
                <div class="text-caption">
                  <span v-if="calculations.discount > 0">
                    {{ formatCurrency(calculations.subtotal) }}
                    <span class="red--text">-{{ formatCurrency(calculations.discount) }}</span>
                  </span>
                  <span v-if="calculations.tax > 0" class="grey--text ml-1">(Imp: {{ formatCurrency(calculations.tax) }})</span>
                </div>
                <strong class="text-h6 primary--text">{{ formatCurrency(calculations.total) }}</strong>
              </div>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <v-card-actions class="grey lighten-4">
        <!-- Botón eliminar si está editando -->
        <v-btn v-if="isEditing" text color="error" @click="deleteProduct">
          <v-icon left>mdi-delete</v-icon>
          Eliminar
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn text @click="closeDialog">Cancelar</v-btn>
        <v-btn
          :color="isEditing ? 'orange' : 'teal'"
          dark
          @click="saveProduct"
          :disabled="!formValid"
          :loading="saving"
        >
          <v-icon left>{{
            isEditing ? "mdi-content-save" : "mdi-cart-plus"
          }}</v-icon>
          {{ isEditing ? "Guardar cambios" : "Añadir al carrito" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { formatCurrency } from "@/services/webserver.js";
import * as countryUtils from "@/utils/countryUtils.js";

export default {
  name: "ManualProduct",
  
  props: {
    value: {
      type: Boolean,
      default: false
    },
    productData: {
      type: Object,
      default: () => ({
        nombre: "",
        descripcion: "",
        note: "",
        cantidad: 1,
        precio: "",
        descuento: 0,
        tax: 21,
        manual: true,
      })
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    editingProductEan: {
      type: String,
      default: null,
      validator(value) {
        // Permitir null (no editando) o string no vacío
        return value === null || (typeof value === 'string' && value.length > 0);
      }
    }
  },

  data() {
    // Obtener configuración dinámica según el país
    const countryCode = countryUtils.getCurrentCountryCode();
    const countrySettings = countryUtils.getCurrentCountrySettings();
    const vatRates = countryUtils.getVatRates();
    
    // Obtener símbolo de moneda desde la configuración del país
    const currencySymbol = countrySettings.country?.currencySymbol || '$';
    const currency = countrySettings.country?.currency || 'COP';
    
    // Debug: Ver qué está retornando
    console.log("🌍 ManualProduct - País detectado:", countryCode);
    console.log("💰 ManualProduct - Configuración del país:", countrySettings);
    console.log("💱 ManualProduct - Símbolo de moneda:", currencySymbol);
    console.log("📊 ManualProduct - Tasas de IVA disponibles:", vatRates);
    
    // Obtener el valor de IVA por defecto según el país
    // Los vat_rates usan 'rate' no 'value' para el porcentaje
    // Para Colombia, el IVA general es 19%, para España 21%, para México 16%
    let defaultTaxRate = 21; // Fallback por defecto
    
    if (vatRates && vatRates.length > 0) {
      // Buscar el IVA marcado como default o el que tenga "General" en el nombre
      const generalRate = vatRates.find(rate => 
        rate.default || 
        rate.name?.toLowerCase().includes('general')
      );
      
      if (generalRate) {
        defaultTaxRate = generalRate.rate;
      } else {
        // Si no hay "General", usar el primero que no sea 0
        const nonZeroRate = vatRates.find(rate => rate.rate > 0);
        defaultTaxRate = nonZeroRate ? nonZeroRate.rate : vatRates[0].rate;
      }
    }
    
    console.log("✅ ManualProduct - IVA por defecto:", defaultTaxRate);

    return {
      formValid: false,
      saving: false,
      
      // Configuración de país y moneda
      countryCode,
      currencySymbol,
      currency,
      countrySettings,
      
      // Producto local para edición
      localProduct: {
        nombre: "",
        descripcion: "",
        note: "",
        cantidad: 1,
        precio: "",
        descuento: 0,
        tax: defaultTaxRate,
        manual: true,
        uom: "UN", // Unidad de medida por defecto
      },

      // Tipos de IVA disponibles (dinámico según país)
      // Mapear 'rate' a 'value' para el v-select
      taxRates: vatRates.map(rate => ({
        text: rate.name,
        value: rate.rate  // Cambiar de rate.value a rate.rate
      })),

      // Reglas de validación
      validationRules: {
        required: [(v) => !!v || "Campo requerido"],
        quantity: [
          (v) => !!v || "Cantidad requerida",
          (v) => v > 0 || "Debe ser mayor a 0",
        ],
        price: [
          (v) => !!v || "Precio requerido",
          (v) => parseFloat(v) > 0 || "Debe ser mayor a 0",
        ],
      },
    };
  },

  computed: {
    showDescriptionField() {
      return !this.isEditing || this.productData.manual;
    },

    calculations() {
      const price = parseFloat(this.localProduct.precio) || 0;
      const qty = parseInt(this.localProduct.cantidad) || 0;
      const discountPercent = parseFloat(this.localProduct.descuento) || 0;
      const taxPercent = parseFloat(this.localProduct.tax) || 0;

      // 1. Calcular por unidad primero
      const discountUnit = (price * discountPercent) / 100;
      const priceWithDiscount = price - discountUnit;

      // 2. Extraer impuesto unitario (ya está incluido en el precio)
      const taxDivisor = 1 + (taxPercent / 100);
      const baseUnit = priceWithDiscount / taxDivisor;
      const taxUnit = priceWithDiscount - baseUnit;

      // 3. Multiplicar por cantidad
      const subtotal = price * qty;
      const discount = discountUnit * qty;
      const tax = taxUnit * qty;
      const total = priceWithDiscount * qty;

      return {
        subtotal,
        discount,
        tax,
        total
      };
    }
  },

  watch: {
    // Sincronizar producto local cuando cambian las props
    productData: {
      handler(newData) {
        // Si no hay un tax definido en newData, usar el valor por defecto del país
        const vatRates = countryUtils.getVatRates();
        let defaultTaxRate = 21;
        
        if (vatRates && vatRates.length > 0) {
          const generalRate = vatRates.find(rate => 
            rate.default || 
            rate.name?.toLowerCase().includes('general')
          );
          
          if (generalRate) {
            defaultTaxRate = generalRate.rate;
          } else {
            const nonZeroRate = vatRates.find(rate => rate.rate > 0);
            defaultTaxRate = nonZeroRate ? nonZeroRate.rate : vatRates[0].rate;
          }
        }
        
        this.localProduct = { 
          ...newData,
          tax: newData.tax !== undefined ? newData.tax : defaultTaxRate
        };
      },
      deep: true,
      immediate: true
    },

    // ✅ Validar editingProductEan cuando cambia
    editingProductEan: {
      handler(newEan, oldEan) {
        console.log("🔍 editingProductEan cambió de", oldEan, "a", newEan);
        
        // Si se está editando, validar que el EAN sea válido
        if (this.isEditing && (!newEan || typeof newEan !== 'string')) {
          console.error("❌ editingProductEan inválido recibido:", newEan);
        }
      },
      immediate: true
    },

    // Resetear formulario cuando se cierra el diálogo
    value(isOpen) {
      if (!isOpen) {
        this.resetForm();
      } else if (isOpen && !this.isEditing) {
        // Si se abre para nuevo producto, limpiar
        this.resetForm();
      }
    }
  },

  methods: {
    // formatCurrency se importa directamente, no necesita método local
    formatCurrency,

    /**
     * Cerrar diálogo
     */
    closeDialog() {
      this.$emit('input', false);
    },

    /**
     * Resetear formulario
     */
    resetForm() {
      // Obtener el valor de IVA por defecto según el país
      const vatRates = countryUtils.getVatRates();
      let defaultTaxRate = 21;
      
      if (vatRates && vatRates.length > 0) {
        const generalRate = vatRates.find(rate => 
          rate.default || 
          rate.name?.toLowerCase().includes('general')
        );
        
        if (generalRate) {
          defaultTaxRate = generalRate.rate;
        } else {
          const nonZeroRate = vatRates.find(rate => rate.rate > 0);
          defaultTaxRate = nonZeroRate ? nonZeroRate.rate : vatRates[0].rate;
        }
      }
      
      this.localProduct = {
        nombre: "",
        descripcion: "",
        note: "",
        cantidad: 1,
        precio: "",
        descuento: 0,
        tax: defaultTaxRate,
        manual: true,
        uom: "UN", // Unidad de medida por defecto
      };
      
      if (this.$refs.productForm) {
        this.$refs.productForm.resetValidation();
      }
      
      this.saving = false;
    },

    /**
     * Incrementar cantidad
     */
    incrementQuantity() {
      this.localProduct.cantidad++;
    },

    /**
     * Decrementar cantidad
     */
    decrementQuantity() {
      if (this.localProduct.cantidad > 1) {
        this.localProduct.cantidad--;
      }
    },

    /**
     * Navegar al siguiente campo con Enter
     */
    focusNext(refName) {
      this.$nextTick(() => {
        if (this.$refs[refName]) {
          this.$refs[refName].focus();
        }
      });
    },

    /**
     * Filtrar solo números
     */
    filterNumbers(evt) {
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      if (charCode > 31 && (charCode < 48 || charCode > 57)) {
        evt.preventDefault();
      }
    },

    /**
     * Filtrar números decimales
     */
    filterDecimals(evt) {
      evt = evt ? evt : window.event;
      const charCode = evt.which ? evt.which : evt.keyCode;
      const value = evt.target.value;

      // Permitir números
      if (charCode >= 48 && charCode <= 57) return true;

      // Permitir un solo punto decimal
      if (charCode === 46 && value.indexOf(".") === -1) return true;

      // Permitir coma y convertir a punto
      if (charCode === 44) {
        if (value.indexOf(".") === -1 && value.indexOf(",") === -1) {
          evt.target.value = value + ".";
        }
        evt.preventDefault();
        return false;
      }

      evt.preventDefault();
      return false;
    },

    /**
     * Guardar producto
     */
    saveProduct() {
      if (!this.$refs.productForm.validate()) {
        return;
      }

      this.saving = true;

      if (this.isEditing) {
        // ✅ Validar que el EAN sea válido antes de emitir
        if (!this.editingProductEan) {
          console.error("❌ EAN de edición inválido:", this.editingProductEan);
          this.saving = false;
          return;
        }

        // Emitir evento de actualización con EAN
        this.$emit('product-updated', {
          product: { ...this.localProduct },
          ean: this.editingProductEan
        });
      } else {
        // Emitir evento de creación
        const product = this.createProductObject();
        this.$emit('product-created', product);
      }

      // Simular delay de guardado
      setTimeout(() => {
        this.saving = false;
        this.closeDialog();
      }, 300);
    },

    /**
     * Eliminar producto
     */
    deleteProduct() {
      // ✅ Validar que el EAN sea válido antes de emitir
      if (!this.editingProductEan) {
        console.error("❌ EAN de eliminación inválido:", this.editingProductEan);
        return;
      }

      this.$emit('product-deleted', {
        ean: this.editingProductEan,
        product: { ...this.localProduct }
      });
      this.closeDialog();
    },

    /**
     * Crear objeto de producto para añadir al carrito
     */
    createProductObject() {
      const now = Date.now();
      const price = parseFloat(this.localProduct.precio);
      const qty = parseInt(this.localProduct.cantidad);
      const discountPercent = parseFloat(this.localProduct.descuento) || 0;

      // Calcular descuento
      const discountAmount = (price * discountPercent) / 100;
      const priceAfterDiscount = price - discountAmount;

      // Calcular precios con impuestos
      const taxRate = parseFloat(this.localProduct.tax) / 100 + 1;
      const priceWithoutTax = Math.round((priceAfterDiscount / taxRate) * 100) / 100;
      const taxAmount = priceAfterDiscount - priceWithoutTax;

      return {
        // Identificadores únicos
        id_pos: window.store?.store_id,
        sku: `MAN-${now}`,
        ean: `MANUAL-${now}`,

        // Información del producto
        nombre: this.localProduct.nombre.toUpperCase(),
        descripcion: this.localProduct.descripcion,
        note: this.localProduct.note,
        categoria: "PRODUCTO MANUAL",
        grupo: "MANUAL",
        marca: "GENÉRICO",
        refe: `REF-${now}`,
        color: "",
        talla: "",
        uom: this.localProduct.uom || "UN", // Unidad de medida

        // Cantidades y precios
        cantidad: qty,
        costo: "0.00",
        precio: priceAfterDiscount,
        price_amount: priceWithoutTax,
        item_tax: taxAmount,
        precioB: "0.00",
        precioC: "0.00",
        descuento: discountPercent,
        discount: discountAmount,

        // Impuestos
        tax: this.localProduct.tax,

        // Información adicional
        store_name: window.seller?.PartyName || "Tienda",
        manual: true,
        isExpanded: false,
      };
    }
  }
};
</script>

<style scoped>
/* Estilos específicos del componente */
.v-dialog .v-card__title {
  border-bottom: 1px solid #e0e0e0;
}

.v-alert {
  border-radius: 8px;
}

/* Mejorar la apariencia de los campos numéricos */
.v-text-field >>> .v-input__append-inner {
  margin-top: 4px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .v-card {
    margin: 0;
    height: 100vh;
    border-radius: 0;
  }
}
</style>
